import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/ui'
import { mtc } from '@/styles'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError, getIsFetchingOrNotStarted } from '@/reducers/fetch'
import { FETCH_RECOMMENDED_OFFERS_KEY } from '@/actions/settings/billingActions'
import { REPLAY_BLOCK_CLASS } from '@/features/amplitude/privacy'

import {
  ANALYTICS_SURFACES,
  REPORT_CTAS,
} from '../analytics/compliance-analytics.constants'
import { useComplianceAnalytics } from '../analytics/use-compliance-analytics'
import { selectRecommendedOffers } from '../compliance.selectors'
import { COMPLIANCE_PATHS, isTaxTreatment } from '../lib/constants'
import { resolveNextStepsCopy } from '../lib/nextStepsCopy'

interface NextStepsActionsProps {
  /** The raw `tax_treatment` answer. Picks the labels and rides along on the CTA
   *  event, one prop rather than two, so the copy shown and the entity reported cannot
   *  be for different practices. */
  taxTreatment: unknown
  /** Scheduler link for the consult CTA; null withholds the CTA entirely. */
  consultUrl: string | null
  /** Qualification treatment, for the CTA event. Passed in rather than read here, so
   *  the report is the single place deciding it. */
  verdict: string
  /** Whether this practice qualifies for a consult; decided by the API at intake submit. */
  isPql: boolean
  /** Layout for the pair — the card stacks them, the sticky bar sits them in a row. */
  className?: string
}

/**
 * The report's two closing actions: book a consult, or go straight to plans.
 *
 * Extracted from `NextStepsCard` so the mobile sticky bar can offer the same pair without
 * a second copy of the rules behind them — which of the two appear, when the plans CTA is
 * withheld, and the replay/analytics handling on the consult link are decided once, here.
 * Two mounted instances is the point: the bar is a shortcut to the card, not a variant of
 * it, so they must never be able to disagree about what is on offer.
 *
 * Copy is resolved here from `taxTreatment` rather than accepted as a prop beside it:
 * passed separately, the two could describe different practices and nothing would catch
 * it. `useNextStepsActions` supplies what both callers pass in.
 */
export const NextStepsActions: React.FC<NextStepsActionsProps> = ({
  taxTreatment,
  consultUrl,
  verdict,
  isPql,
  className,
}) => {
  const copy = resolveNextStepsCopy(taxTreatment)
  const navigate = useNavigate()
  const { trackClickedReportCta } = useComplianceAnalytics(
    ANALYTICS_SURFACES.SignupFunnel
  )
  const offersResult = useReselector(selectRecommendedOffers)
  const offersPending = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_RECOMMENDED_OFFERS_KEY
  )
  const offersError = useReselector(getFetchError, FETCH_RECOMMENDED_OFFERS_KEY)
  const reportedTaxTreatment = isTaxTreatment(taxTreatment)
    ? taxTreatment
    : null

  // Everything but a resolved, empty list keeps the link, because a blip must never
  // look like an ineligible practice. A failed fetch keeps it too: a stored empty list
  // predates that failure, so it is no longer a verdict worth withholding the link over.
  const resolvedEmpty =
    !offersError &&
    offersResult?.status === 'ok' &&
    offersResult.offers.length === 0
  const plansCtaLoading = offersResult === null && offersPending

  // A non-PQL practice whose offers resolve empty has neither action. Nothing rather than
  // an empty box, so the sticky bar's `empty:hidden` takes its chrome away with it.
  if (!consultUrl && resolvedEmpty) return null

  return (
    <div className={mtc('flex flex-col gap-2', className)}>
      {consultUrl && (
        <Button asChild variant="primary" className="w-full">
          {/* The href carries the practitioner's name and email, and neither replay
              backend masks attribute values — so this link is excluded from both.
              Blocking hides it from replay entirely, which is why the click is
              tracked explicitly below. */}
          <a
            href={consultUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-dd-privacy="hidden"
            className={REPLAY_BLOCK_CLASS}
            onClick={() =>
              trackClickedReportCta({
                cta: REPORT_CTAS.Consult,
                verdict,
                is_pql: isPql,
                tax_treatment: reportedTaxTreatment,
              })
            }
          >
            {copy.consultLabel}
          </a>
        </Button>
      )}
      {!resolvedEmpty && (
        <Button
          variant={consultUrl ? 'secondary' : 'primary'}
          className="w-full"
          loading={plansCtaLoading}
          onClick={() => {
            trackClickedReportCta({
              cta: REPORT_CTAS.Plans,
              verdict,
              is_pql: isPql,
              tax_treatment: reportedTaxTreatment,
            })
            navigate(COMPLIANCE_PATHS.planPricing)
          }}
        >
          {copy.plansLabel}
        </Button>
      )}
    </div>
  )
}
